/**
 * Flow timeline — bottom bar showing the whole chart on one row.
 *
 * Each note is rendered as a thin vertical mark coloured by its lane,
 * stacked into 4 sub-rows so the user can see which lane is busy.
 * Click anywhere on the timeline to seek the playhead.
 *
 * Selection overlay: white fill at 25% opacity + marching-ants dashed
 * border around the time range of the selected notes.
 */

import React, { useRef, useEffect, useCallback, useMemo } from 'react';
import { useFlowStore } from './flowStore';
import { useTheme } from '../../components/ui/themeStore';
import { tintOklch } from '../../components/ui/themeColors';
import { FLOW_LANE_COLORS, laneCountFor } from './types';
import { analyzeFlowDifficulty, type ComplexityFlag } from './flowDifficultyAnalyzer';
import { color } from '../../components/ui/theme';

// Strip height matches the playback pill row (48px) so the waveform reads as a
// sibling of the transport bar above it. The drawable content (lanes + waveform)
// is inset by PAD_Y top & bottom so the peaks never crowd the rounded border.
const PAD_Y = 8;
const TOTAL_HEIGHT = 48;
const CONTENT_H = TOTAL_HEIGHT - PAD_Y * 2;
const TOP = PAD_Y;
// Marching-ants dash pattern: [dash, gap] in px
const DASH = 5;
const GAP  = 3;
const PERIOD = DASH + GAP;

export default function FlowTimeline() {
  const canvasRef    = useRef<HTMLCanvasElement>(null);
  const wrapperRef   = useRef<HTMLDivElement>(null);
  const playheadRef  = useRef<HTMLDivElement>(null);
  const rafRef       = useRef<number>(0);
  const dashOffsetRef = useRef(0);

  // ── Area-select state (Shift+drag draws a band, Shift+scroll extends it) ───
  // bandRef     = the provisional purple band drawn on the canvas (seconds).
  // selRangeRef = the committed range Shift+scroll grows/shrinks from.
  // draggingRef = a Shift+drag is in progress; didDragRef suppresses the click
  //               (= seek) that fires right after a drag finishes.
  const bandRef      = useRef<{ a: number; b: number } | null>(null);
  const selRangeRef  = useRef<{ a: number; b: number } | null>(null);
  const draggingRef  = useRef(false);
  const didDragRef   = useRef(false);

  const chart    = useFlowStore(s => s.chart);
  const duration = useFlowStore(s => s.duration);
  const seek     = useFlowStore(s => s.seek);
  const audioUrl = useFlowStore(s => s.audioUrl);
  const activeDifficulty = useFlowStore(s => s.activeDifficulty);
  // Active lane count (3 or 4). The strip height stays fixed; rows just get
  // taller for 3 lanes so they fill the same area.
  const laneCount = useFlowStore(s => laneCountFor(s.chart, s.activeDifficulty));
  // Configurable lane colours (same source as the 3D highway + minimap) so a
  // colour change in settings recolours the timeline notes too — not hardcoded.
  const laneColors = useFlowStore(s => s.gameplaySettings.laneColors);
  // Right-drag loop region (teal band).
  const loopRegion = useFlowStore(s => s.loopRegion);

  // Difficulty complexity overlay: flag sections that read as too hard for the
  // tier currently being mapped. Toggleable so it doesn't crowd the timeline
  // when the user just wants the plain note view.
  // Shared with the transport panel + 3D overlay via the store.
  const showComplexity = useFlowStore(s => s.showComplexity);
  const setShowComplexity = useFlowStore(s => s.setShowComplexity);
  // Re-paint when the tint theme changes (waveform/grid use the tint hue).
  const themeTint = useTheme(s => s.tint);
  const themeTintHex = useTheme(s => s.tintHex);
  const complexityFlags: ComplexityFlag[] = useMemo(() => {
    if (!showComplexity) return [];
    return analyzeFlowDifficulty(chart.notes, chart.bpm, activeDifficulty).flags;
  }, [showComplexity, chart.notes, chart.bpm, activeDifficulty]);

  // Waveform peaks (max abs amplitude per bucket) + the decoded buffer's true
  // duration, so we can draw the audio behind the notes and the user can SEE
  // whether the beat grid / notes line up with the music's transients.
  const [peaks, setPeaks] = React.useState<{ data: Float32Array; dur: number } | null>(null);

  useEffect(() => {
    if (!audioUrl) { setPeaks(null); return; }
    let alive = true;
    (async () => {
      try {
        const buf = await (await fetch(audioUrl)).arrayBuffer();
        const Ctx: typeof AudioContext =
          window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        const ctx = new Ctx();
        const audio = await ctx.decodeAudioData(buf);
        ctx.close().catch(() => {});
        const ch = audio.getChannelData(0);
        const BUCKETS = 2048;
        const per = Math.max(1, Math.floor(ch.length / BUCKETS));
        const out = new Float32Array(BUCKETS);
        for (let b = 0; b < BUCKETS; b++) {
          let peak = 0;
          const base = b * per;
          for (let i = 0; i < per; i++) {
            const v = Math.abs(ch[base + i] || 0);
            if (v > peak) peak = v;
          }
          out[b] = peak;
        }
        if (alive) {
          setPeaks({ data: out, dur: audio.duration });
          // The decoded buffer duration is EXACT (unlike HTMLAudioElement's
          // VBR-MP3 estimate). Make it authoritative so the playhead + notes
          // are scaled to the song's true length — otherwise the timeline
          // sweeps faster (or slower) than the music.
          const store = useFlowStore.getState();
          if (
            Number.isFinite(audio.duration) && audio.duration > 0
            && Math.abs(store.duration - audio.duration) > 0.05
          ) {
            store.setDuration(audio.duration);
          }
        }
      } catch {
        if (alive) setPeaks(null);
      }
    })();
    return () => { alive = false; };
  }, [audioUrl]);

  // ── Static canvas paint ────────────────────────────────────────────────────
  // Called on chart / duration changes. Also called by the RAF loop (passing
  // the current dash offset) to animate the marching-ants selection border.
  const paint = useCallback((dashOffset = 0) => {
    const canvas  = canvasRef.current;
    const wrapper = wrapperRef.current;
    if (!canvas || !wrapper) return;

    const dpr  = window.devicePixelRatio || 1;
    const cssW = wrapper.clientWidth;
    if (cssW <= 0) return;
    const cssH = TOTAL_HEIGHT;
    if (canvas.width  !== Math.floor(cssW * dpr) ||
        canvas.height !== Math.floor(cssH * dpr)) {
      canvas.width  = Math.floor(cssW * dpr);
      canvas.height = Math.floor(cssH * dpr);
      canvas.style.width  = cssW + 'px';
      canvas.style.height = cssH + 'px';
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cssW, cssH);

    // Per-lane row height: the lane AREA stays fixed (FLOW_LANE_COUNT*ROW_HEIGHT)
    // so the strip never changes size; with 3 lanes each row is just taller.
    const rowH = CONTENT_H / laneCount;

    // ONE uniform, opaque dark fill edge-to-edge — a single container, not a
    // bluish outer strip with a darker inner box. Opaque so the 3D background
    // can't tint it. The waveform stays inset (PAD_Y) so it never touches the
    // rounded border, but the fill itself is flush to the pill edges.
    ctx.fillStyle = tintOklch(0.075, 0.016, 1);
    ctx.fillRect(0, 0, cssW, cssH);

    const songDur = Math.max(duration, 0.01);

    // Inset the time-mapped CONTENT (waveform, notes, grid, overlays) by the
    // pill's corner radius so it starts/ends where the straight edge begins —
    // never under the round caps. The dark fill above stays full-width.
    const PAD_X = TOTAL_HEIGHT / 2;
    const drawW = Math.max(1, cssW - PAD_X * 2);
    const timeToX = (t: number) => PAD_X + (t / songDur) * drawW;

    // ── Audio waveform (behind everything) ─────────────────────────────────
    // Draw the real audio peaks so the user can visually verify the beat grid
    // / notes land on the music. Mapped by the SAME time→x scale as notes and
    // the playhead, so if a note doesn't sit on its transient, the bpm/offset
    // is off (not a render bug).
    if (peaks) {
      const { data, dur } = peaks;
      const midY = TOP + CONTENT_H / 2;
      const halfH = CONTENT_H / 2 - 1;
      ctx.fillStyle = tintOklch(0.58, 0.11, 0.30);
      for (let b = 0; b < data.length; b++) {
        const t = (b / data.length) * dur;
        const x = timeToX(t);
        if (x < PAD_X || x > cssW - PAD_X) continue;
        const h = data[b] * halfH;
        ctx.fillRect(x, midY - h, 1, h * 2);
      }
    }

    // Compute each note's audio time LIVE from beats + bpm + offset (never the
    // cached time_seconds, which can be stale). This is the exact same model
    // the 3D scene + playhead use, so the three views can't disagree:
    //   audioTime(beat) = offset + beat * secPerBeat
    const secPerBeat = 60 / Math.max(1, chart.bpm);
    const offsetSec  = chart.offset_seconds;
    const beatToSec  = (beat: number) => offsetSec + beat * secPerBeat;

    // ── Difficulty complexity bands (behind the notes) ─────────────────────
    // Amber = a touch hot for the tier, red = clearly too hard. Drawn under the
    // notes so the lane marks stay readable on top.
    if (complexityFlags.length > 0) {
      const h = CONTENT_H;
      for (const f of complexityFlags) {
        const x1 = timeToX(beatToSec(f.startBeat));
        const x2 = Math.max(x1 + 3, timeToX(beatToSec(f.endBeat)));
        const over = f.severity === 'over';
        ctx.fillStyle = over ? 'rgba(239, 68, 68, 0.20)' : 'rgba(234, 179, 8, 0.18)';
        ctx.fillRect(x1, TOP, x2 - x1, h);
        // Top stripe so a thin section is still visible.
        ctx.fillStyle = over ? 'rgba(239, 68, 68, 0.85)' : 'rgba(234, 179, 8, 0.85)';
        ctx.fillRect(x1, TOP, x2 - x1, 2);
      }
    }

    // Notes
    for (const note of chart.notes) {
      const x = timeToX(beatToSec(note.time_beats));
      if (x < PAD_X - 2 || x > cssW - PAD_X + 2) continue;
      const y = TOP + note.lane * rowH;
      const laneColor = laneColors[note.lane] || FLOW_LANE_COLORS[note.lane];
      ctx.fillStyle = laneColor;
      ctx.fillRect(x - 1, y + 1, 2, rowH - 2);
      if (
        note.type === 'arrow-left' ||
        note.type === 'arrow-center' ||
        note.type === 'arrow-right'
      ) {
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(x - 1, y, 2, 2);
      }
      if (note.duration_beats > 0) {
        const endSec = beatToSec(note.time_beats + note.duration_beats);
        const xEnd   = timeToX(endSec);
        ctx.fillStyle = laneColor;
        ctx.globalAlpha = 0.3;
        ctx.fillRect(x, y + rowH * 0.35, Math.max(1, xEnd - x), rowH * 0.3);
        ctx.globalAlpha = 1;
      }
    }

    // Bookmarks — golf-hole FLAGS (reviewer ask): a pole down the strip with a
    // little pennant + the bookmark's name at the top, readable mid-playback.
    const bookmarks = chart.bookmarks ?? [];
    if (bookmarks.length > 0) {
      ctx.setLineDash([]);
      ctx.font = '600 8px ui-monospace, Menlo, Consolas, monospace';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      for (const b of bookmarks) {
        const x = timeToX(beatToSec(b.time_beats));
        if (x < PAD_X - 4 || x > cssW - PAD_X + 40) continue;
        // Pole
        ctx.strokeStyle = 'rgba(251, 191, 36, 0.65)';
        ctx.lineWidth = 1.4;
        ctx.beginPath();
        ctx.moveTo(x, TOP);
        ctx.lineTo(x, TOP + CONTENT_H);
        ctx.stroke();
        ctx.lineWidth = 1;
        // Pennant (right-pointing triangle off the pole top)
        ctx.fillStyle = '#FBBF24';
        ctx.beginPath();
        ctx.moveTo(x, TOP);
        ctx.lineTo(x + 9, TOP + 3.5);
        ctx.lineTo(x, TOP + 7);
        ctx.closePath();
        ctx.fill();
        // Name beside the pennant (dark backing chip so it reads on waveform)
        if (b.label) {
          const label = b.label.slice(0, 14);
          const w = ctx.measureText(label).width;
          ctx.fillStyle = 'rgba(10, 6, 22, 0.78)';
          ctx.fillRect(x + 10, TOP, w + 6, 9);
          ctx.fillStyle = '#FBBF24';
          ctx.fillText(label, x + 13, TOP + 4.5);
        }
      }
    }

    // Beat / measure grid (user feedback: mark every beat, thick downbeats).
    // Adaptive so long songs don't drown in lines: beats draw only when they're
    // ≥3px apart; measures (every 4 beats) when ≥3px; else fall back to 30s ticks.
    const gridSecPerBeat = 60 / Math.max(1, chart.bpm);
    const pxPerBeat = Math.abs(timeToX(gridSecPerBeat) - timeToX(0));
    ctx.setLineDash([]);
    if (pxPerBeat * 4 >= 3) {
      const totalBeats = Math.ceil((duration - chart.offset_seconds) / gridSecPerBeat);
      const drawBeats = pxPerBeat >= 3;
      // Bar NUMBERS every measure once there's room (reviewer: "the bars look
      // the same as before; the bar number should appear") — density adapts:
      // every measure ≥26px apart, else every 2nd/4th so labels never collide.
      const pxPerMeasure = pxPerBeat * 4;
      const labelEvery = pxPerMeasure >= 26 ? 1 : pxPerMeasure >= 13 ? 2 : pxPerMeasure >= 7 ? 4 : 0;
      ctx.font = '600 8px ui-monospace, Menlo, Consolas, monospace';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      for (let b = 0; b <= totalBeats; b++) {
        const isMeasure = b % 4 === 0;
        if (!isMeasure && !drawBeats) continue;
        const x = timeToX(beatToSec(b));
        if (x < PAD_X - 1 || x > cssW - PAD_X + 1) continue;
        // Downbeats read clearly heavier + brighter than plain beats.
        ctx.strokeStyle = isMeasure ? tintOklch(0.72, 0.11, 0.60) : tintOklch(0.55, 0.09, 0.14);
        ctx.lineWidth = isMeasure ? 2.2 : 1;
        ctx.beginPath();
        ctx.moveTo(x, TOP);
        ctx.lineTo(x, TOP + CONTENT_H);
        ctx.stroke();
        if (isMeasure && labelEvery > 0 && (b / 4) % labelEvery === 0) {
          ctx.fillStyle = tintOklch(0.78, 0.10, 0.85);
          ctx.fillText(String(b), x + 2, TOP + 1);
        }
      }
      ctx.lineWidth = 1;
    } else {
      const tickEvery = 30;
      ctx.strokeStyle = tintOklch(0.55, 0.09, 0.22);
      for (let s = tickEvery; s <= duration; s += tickEvery) {
        const x = timeToX(s);
        ctx.beginPath();
        ctx.moveTo(x, TOP);
        ctx.lineTo(x, TOP + CONTENT_H);
        ctx.stroke();
      }
    }

    // ── Selection overlay ──────────────────────────────────────────────────
    // Read live from store so the RAF-driven animation never sees a stale
    // React-closure snapshot of the selection set.
    const currentSelectedIds = useFlowStore.getState().selection.noteIds;
    if (currentSelectedIds.size > 0) {
      let minSec = Infinity, maxSec = -Infinity;
      for (const note of chart.notes) {
        if (!currentSelectedIds.has(note.id)) continue;
        const startSec = beatToSec(note.time_beats);
        minSec = Math.min(minSec, startSec);
        const endSec = note.duration_beats > 0
          ? beatToSec(note.time_beats + note.duration_beats)
          : startSec;
        maxSec = Math.max(maxSec, endSec);
      }

      if (minSec < Infinity) {
        const x1 = timeToX(minSec);
        // Single tap → give it at least 4 px so the outline is visible.
        const x2 = Math.max(x1 + 4, timeToX(maxSec));
        const h  = CONTENT_H;

        // White fill at 25% opacity
        ctx.fillStyle = 'rgba(255, 255, 255, 0.25)';
        ctx.fillRect(x1, TOP, x2 - x1, h);

        // Marching-ants dashed outline
        ctx.save();
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.90)';
        ctx.lineWidth   = 1.5;
        ctx.setLineDash([DASH, GAP]);
        ctx.lineDashOffset = -dashOffset;
        const inset = 0.75;
        ctx.strokeRect(
          x1 + inset,
          TOP + inset,
          Math.max(1, x2 - x1 - inset * 2),
          h - inset * 2,
        );
        ctx.restore();
      }
    }

    // ── Area-select band (Shift+drag / Shift+scroll) ──────────────────────
    // Same look as the note-selection overlay: white 25% fill + marching-ants
    // dashed border, so the demarcated time range reads identically whether or
    // not it currently contains notes.
    const band = bandRef.current;
    if (band) {
      const bx1 = timeToX(Math.min(band.a, band.b));
      const bx2 = Math.max(bx1 + 4, timeToX(Math.max(band.a, band.b)));
      const bh  = CONTENT_H;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.25)';
      ctx.fillRect(bx1, TOP, bx2 - bx1, bh);
      ctx.save();
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.90)';
      ctx.lineWidth   = 1.5;
      ctx.setLineDash([DASH, GAP]);
      ctx.lineDashOffset = -dashOffset;
      const inset = 0.75;
      ctx.strokeRect(bx1 + inset, TOP + inset, Math.max(1, bx2 - bx1 - inset * 2), bh - inset * 2);
      ctx.restore();
    }

    // ── Loop region (right-drag) — teal band ───────────────────────────────
    if (loopRegion) {
      const lx1 = timeToX(beatToSec(loopRegion.startBeat));
      const lx2 = Math.max(lx1 + 2, timeToX(beatToSec(loopRegion.endBeat)));
      const lh = CONTENT_H;
      ctx.fillStyle = 'rgba(45, 212, 191, 0.22)';  // teal
      ctx.fillRect(lx1, TOP, lx2 - lx1, lh);
      ctx.save();
      ctx.setLineDash([DASH, GAP]);
      ctx.lineDashOffset = -dashOffset;
      ctx.strokeStyle = 'rgba(45, 212, 191, 0.95)';
      ctx.lineWidth = 1.5;
      ctx.strokeRect(lx1 + 0.75, TOP + 0.75, Math.max(1, lx2 - lx1 - 1.5), lh - 1.5);
      ctx.restore();
    }

    // (Offset/pre-roll marker removed — the vertical downbeat line + dim veil
    // that used to draw at the start read as a stray marker, so the strip now
    // shows only the waveform + notes.)
  }, [chart, duration, peaks, complexityFlags, laneColors, loopRegion, laneCount, themeTint, themeTintHex]);

  // Repaint on chart / duration changes
  useEffect(() => { paint(); }, [paint]);

  // Repaint when selection changes (paint reads store live, so any selection
  // change must trigger a fresh canvas draw to show/hide the overlay).
  useEffect(() => {
    return useFlowStore.subscribe((state, prev) => {
      if (state.selection.noteIds !== prev.selection.noteIds) {
        // NOTE: don't clear the area band here — Shift+scroll/drag legitimately
        // sweeps over empty regions (selection momentarily empty) while the band
        // is still being demarcated. The band is cleared on a plain click.
        paintRef.current();
      }
    });
  }, []);

  // Repaint on resize
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const ro = new ResizeObserver(() => paint());
    ro.observe(wrapper);
    return () => ro.disconnect();
  }, [paint]);

  // ── RAF loop — playhead + marching-ants animation ─────────────────────────
  const paintRef = useRef(paint);
  useEffect(() => { paintRef.current = paint; }, [paint]);

  useEffect(() => {
    const loop = () => {
      // Move playhead div (cheap DOM update, no canvas repaint)
      const wrapper  = wrapperRef.current;
      const playhead = playheadRef.current;
      if (wrapper && playhead) {
        const state  = useFlowStore.getState();
        const cssW   = wrapper.clientWidth;
        const songDur = Math.max(state.duration, 0.01);
        const padX = TOTAL_HEIGHT / 2;
        const drawW = Math.max(1, cssW - padX * 2);
        // Clamp inside the waveform region so the marker never sits over the
        // empty rounded caps where there's no waveform.
        const x = Math.max(padX, Math.min(cssW - padX, padX + (state.currentTime / songDur) * drawW));
        playhead.style.left = `${x}px`;
      }

      // Animate marching ants whenever something is selected OR an area band
      // is being demarcated (Shift+drag / Shift+scroll), even over empty space.
      if (useFlowStore.getState().selection.noteIds.size > 0 || bandRef.current || useFlowStore.getState().loopRegion) {
        dashOffsetRef.current = (dashOffsetRef.current + 0.35) % PERIOD;
        paintRef.current(dashOffsetRef.current);
      }

      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  // Map a viewport X to a song time (seconds), clamped to [0, duration].
  const xToSec = useCallback((clientX: number) => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return 0;
    const rect  = wrapper.getBoundingClientRect();
    // Match the canvas inset: time maps within [PAD_X, width - PAD_X].
    const padX = TOTAL_HEIGHT / 2;
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left - padX) / Math.max(1, rect.width - padX * 2)));
    return ratio * Math.max(useFlowStore.getState().duration, 0.01);
  }, []);

  // Select every note whose start time falls inside [aSec, bSec], AND record
  // the dragged span as a beat SECTION (even if it contains no notes) so the
  // copilot's "Map this section" knows the range.
  const selectRange = useCallback((aSec: number, bSec: number) => {
    const lo = Math.min(aSec, bSec), hi = Math.max(aSec, bSec);
    const st = useFlowStore.getState();
    const secPerBeat = 60 / Math.max(1, st.chart.bpm);
    const offs = st.chart.offset_seconds;
    const ids = st.chart.notes
      .filter(n => { const s = offs + n.time_beats * secPerBeat; return s >= lo && s <= hi; })
      .map(n => n.id);
    st.selectNotes(ids, false);
    st.setSelectedSection({
      startBeat: Math.max(0, (lo - offs) / secPerBeat),
      endBeat: Math.max(0, (hi - offs) / secPerBeat),
    });
  }, []);

  // Left-drag (with or without Shift) → select a time SECTION/area. A plain
  // click (no movement past the threshold) still seeks the playhead. No notes
  // are required — the dragged span is recorded as the section regardless.
  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;         // left button only
    const startSec = xToSec(e.clientX);
    const startX = e.clientX;
    draggingRef.current = true;
    didDragRef.current  = false;
    selRangeRef.current = null;
    let committed = false;              // crossed the move threshold → it's a drag
    const onMove = (ev: MouseEvent) => {
      if (!draggingRef.current) return;
      if (!committed && Math.abs(ev.clientX - startX) < 4) return; // tiny move = still a click
      committed = true;
      didDragRef.current = true;
      const cur = xToSec(ev.clientX);
      bandRef.current = { a: startSec, b: cur };
      selectRange(startSec, cur);
      paintRef.current();
    };
    const onUp = (ev: MouseEvent) => {
      draggingRef.current = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      if (!committed) return;           // never moved → let the click handler seek
      const cur = xToSec(ev.clientX);
      bandRef.current     = { a: startSec, b: cur };
      selRangeRef.current = { a: startSec, b: cur };
      selectRange(startSec, cur);
      paintRef.current();
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [xToSec, selectRange]);

  const handleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (didDragRef.current) {           // swallow the click that ends an area drag
      didDragRef.current = false;
      return;
    }
    // A plain click seeks and dismisses any lingering area band + section.
    bandRef.current = null;
    selRangeRef.current = null;
    useFlowStore.getState().setSelectedSection(null);
    seek(xToSec(e.clientX));
  }, [seek, xToSec]);

  // Double-click near a bookmark flag → name / rename it (reviewer ask:
  // bookmarks should be nameable). Anywhere else the double-click is inert.
  const handleDoubleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const st = useFlowStore.getState();
    const marks = st.chart.bookmarks ?? [];
    if (marks.length === 0) return;
    const sec = xToSec(e.clientX);
    const secPerBeat = 60 / Math.max(1, st.chart.bpm);
    const near = marks.reduce((best: { d: number; b: (typeof marks)[number] } | null, b) => {
      const d = Math.abs(b.time_beats * secPerBeat + st.chart.offset_seconds - sec);
      return !best || d < best.d ? { d, b } : best;
    }, null);
    if (!near || near.d > secPerBeat * 2) return;
    const label = window.prompt('Bookmark name:', near.b.label ?? '');
    if (label === null) return;
    st.renameBookmark(near.b.time_beats, label.trim());
  }, [xToSec]);

  // Shift+scroll → grow / shrink the selected area by one beat per wheel tick.
  // Native non-passive listener so we can preventDefault the page scroll.
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.shiftKey) return;
      e.preventDefault();
      const st  = useFlowStore.getState();
      const dur = Math.max(st.duration, 0.01);
      const secPerBeat = 60 / Math.max(1, st.chart.bpm);
      // Extend the committed range, or start a fresh one at the playhead.
      const range = selRangeRef.current ?? { a: st.currentTime, b: st.currentTime };
      const dir   = e.deltaY < 0 ? 1 : -1;          // scroll up grows forward
      const b     = Math.max(0, Math.min(dur, range.b + dir * secPerBeat));
      const next  = { a: range.a, b };
      selRangeRef.current = next;
      bandRef.current     = next;
      selectRange(next.a, next.b);
      paintRef.current();
    };
    wrapper.addEventListener('wheel', onWheel, { passive: false });
    return () => wrapper.removeEventListener('wheel', onWheel);
  }, [selectRange]);

  return (
    <div ref={wrapperRef} onMouseDown={handleMouseDown} onClick={handleClick} onDoubleClick={handleDoubleClick} style={wrapperStyle}>
      <canvas ref={canvasRef} style={canvasStyle} />
      <div ref={playheadRef} style={playheadStyle} />
      <button
        type="button"
        title={`Difficulty check (${activeDifficulty}) — highlights sections too hard for this tier`}
        onClick={(e) => { e.stopPropagation(); setShowComplexity(!showComplexity); }}
        style={{
          ...complexityToggleStyle,
          opacity: showComplexity ? 1 : 0.5,
          borderColor: showComplexity ? 'rgba(234, 179, 8, 0.7)' : 'rgba(120, 80, 220, 0.3)',
        }}
      >
        {showComplexity && complexityFlags.length > 0 ? `⚠ ${complexityFlags.length}` : '⚠'}
      </button>
    </div>
  );
}

const wrapperStyle: React.CSSProperties = {
  position: 'relative',
  // Full content width so the waveform lines up with the pill row above it.
  width: '100%',
  height: TOTAL_HEIGHT,
  borderRadius: TOTAL_HEIGHT / 2,   // fully round (pill) ends
  border: '1px solid rgba(120, 80, 220, 0.18)',
  overflow: 'hidden',
  cursor: 'pointer',
  // Opaque dark matching the canvas fill (tintOklch 0.075) so the rounded
  // corners read as the same single container — no second/bluish frame.
  background: 'oklch(0.075 calc(0.016 * var(--tint-c)) var(--tint-h))',
};

const canvasStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  height: TOTAL_HEIGHT,
};

const playheadStyle: React.CSSProperties = {
  position: 'absolute',
  // Span only the waveform CONTENT band (inset by PAD_Y) so the marker never
  // sticks out above/below the waveform into the rounded caps.
  top: PAD_Y,
  height: CONTENT_H,
  width: 2,
  marginLeft: -1,
  background: color.accent,
  boxShadow: '0 0 6px rgba(217, 70, 239, 0.6)',
  pointerEvents: 'none',
};

const complexityToggleStyle: React.CSSProperties = {
  position: 'absolute',
  top: 3,
  right: 4,
  height: 16,
  minWidth: 16,
  padding: '0 4px',
  fontSize: 10,
  lineHeight: '14px',
  color: '#EAB308',
  background: 'rgba(10, 8, 24, 0.85)',
  border: '1px solid rgba(234, 179, 8, 0.7)',
  borderRadius: 4,
  cursor: 'pointer',
  zIndex: 6,
};
